import axios from 'axios';
import { getAccountId } from './account';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Signing in and managing the connections themselves are not about one
// account, so they must not be narrowed to whichever one is being viewed.
const UNSCOPED = [/^\/auth\//, /^\/accounts(\/|$)/, /^\/meli\/(auth|callback)/];

// Create axios instance
// No default Content-Type: axios sets application/json for object payloads
// on its own, and a JSON default makes it silently convert FormData uploads
// into a JSON body — the file disappears and the server sees no fields at all.
const api = axios.create({
  baseURL: API_BASE,
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('meli_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Every screen reads whatever account is selected. Doing it here instead
    // of call by call is what keeps two brands' catalogues from turning up in
    // the same list the moment a page forgets to pass it.
    const accountId = getAccountId();
    const url = config.url || '';
    const scoped = accountId && !UNSCOPED.some((pattern) => pattern.test(url));
    if (scoped && !config.params?.account_id) {
      config.params = { ...(config.params || {}), account_id: accountId };
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('meli_token');
      localStorage.removeItem('meli_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (name, email, password, country) => {
    const response = await api.post('/auth/register', {
      name,
      email,
      password,
      country,
    });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Mercado Libre API
export const meliAPI = {
  getAuthUrl: async (country, accountName = 'Cuenta Principal') => {
    const response = await api.get('/meli/auth-url', {
      params: { country, account_name: accountName },
    });
    return response.data.auth_url;
  },

  getStatus: async () => {
    const response = await api.get('/meli/status');
    return response.data;
  },

  refreshToken: async () => {
    const response = await api.post('/meli/refresh');
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  getProducts: async (limit = 20, offset = 0) => {
    const response = await api.get(`/dashboard/products?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  getAlerts: async () => {
    const response = await api.get('/dashboard/alerts');
    return response.data;
  },
};

// Sync API
export const syncAPI = {
  trigger: async () => {
    const response = await api.post('/sync/trigger');
    return response.data;
  },

  // One background run that walks the connected accounts in turn. Never one
  // request per account: a full sync is over a thousand calls to Mercado
  // Libre on its own.
  triggerAll: async () => {
    const response = await api.post('/sync/all-accounts');
    return response.data;
  },
};

// Accounts API
export const accountsAPI = {
  list: async () => {
    const response = await api.get('/accounts/list');
    return response.data;
  },

  getActive: async () => {
    const response = await api.get('/accounts/active');
    return response.data;
  },

  setActive: async (accountId) => {
    const response = await api.post(`/accounts/set-active/${accountId}`);
    return response.data;
  },

  delete: async (accountId) => {
    const response = await api.delete(`/accounts/${accountId}`);
    return response.data;
  },
};

export default api;
